import React, { createContext, useContext, useEffect, useState } from 'react';
import { env } from '../utils/util';
import { getJSON } from '../utils/json';

export const WebSocketContext = createContext(null);

const host = env('REACT_APP_TRIP_COMPANY_API_SERVER_URL').trim().replace(/(http:)/g, 'ws:').replace(/(https:)/g, 'wss:');
const url = `${host}${host.endsWith('/')?'':'/'}ws`;

export const WebSocketProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [needReconnect, setNeedReconnect] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [registeredWS, setRegisteredWS] = useState(0);
  const [subscriptions, setSubscriptions] = useState({});
  const [reconnectTimeout, setReconnectTimeout] = useState(null);


  const connect = (wsId) => {
    if (isConnected) {
      return;
    }

    const ws = new WebSocket(url);
    ws.id = wsId;

    ws.onopen = () => {
      if (getRegisteredWS() === wsId) {
        setIsConnected(true);
        setNeedReconnect(false);
        console.log('WebSocket connected', ws.id);
        subscribeAll(ws);
      } else {
        ws.close();
      }
    };

    ws.onmessage = (event) => {
      let message = getJSON(event.data);
      if (typeof message === 'object') {
        onTopic(message.topic, message.data);
      }
    };

    ws.onclose = (e) => {
      console.log('WebSocket disconnected', ws.id, e);
      setIsConnected(false);
      if (e.code !== 1005) {
        setNeedReconnect(needReconnect + 1);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', ws.id, error);
    };

    setClient(ws);

    return ws;
  };

  const reconnect = () => {
    if (client && !isConnected) {
      setRegisteredWS(registeredWS + 1);
    }
  };

  const disconnect = () => {
    clearTimeout(reconnectTimeout);

    unsubscribeAll(client);

    if (client) {
      client.close();
    }
  };

  const beforeUnload = () => {
    disconnect();
  };

  const getRegisteredWS = () => {
    return registeredWS;
  }


  const subscribe = (topic, callback) => {
    subscriptions[topic] = subscriptions[topic] ?? []
    let subscribers = subscriptions[topic];

    if (client && isConnected && subscribers.length === 0) {
      try {
        client.send(JSON.stringify({
          cmd: 'subscribe',
          topic: topic,
        }));
      } catch(e) {}
    }

    subscribers.push(callback);

    setSubscriptions({ ...subscriptions, [topic]: subscribers });
  };

  const subscribeAll = (client) => {
    if (client) {
      for (let topic in subscriptions) {
        subscriptions[topic] = subscriptions[topic] ?? []
        let subscribers = subscriptions[topic];

        if (subscribers.length !== 0) {
          try {
            client.send(JSON.stringify({
              cmd: 'subscribe',
              topic: topic,
            }));
          } catch(e) {}
        }
      }
    }
  };

  const unsubscribe = (topic, callback) => {
    let subscribers = subscriptions[topic] ?? [];
    let newSubscribers = subscribers.filter((sub) => sub !== callback);

    if (client && isConnected && newSubscribers.length === 0) {
      try {
        client.send(JSON.stringify({
          cmd: 'unsubscribe',
          topic: topic,
        }));
      } catch(e) {}
    } else {
      console.warn('WebSocket not connected. Message not sent.');
    }

    setSubscriptions({ ...subscriptions, [topic]: newSubscribers });
  };

  const unsubscribeAll = (client) => {
    if (client) {
      for (let topic in subscriptions) {
        let subscribers = subscriptions[topic] ?? [];

        if (subscribers.length !== 0) {
          try {
            client.send(JSON.stringify({
              cmd: 'unsubscribe',
              topic: topic,
            }));
          } catch(e) {}
        }
      }
    }

    setSubscriptions({});
  };

  const onTopic = (topic, message) => {
    let subscribers = subscriptions[topic] ?? [];
    if (subscribers.length > 0) {
      subscribers.forEach((sub) => {
        try {
          sub(message);
        } catch(e) {}
      });
    }
  };

  useEffect(() => {
    let ws = null;

    if (registeredWS > 0) {
      ws = connect(registeredWS);
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [registeredWS]);

  useEffect(() => {
    if (needReconnect > 0 && !isConnected) {
      setReconnectTimeout(setTimeout(() => {
        reconnect();
      }, 3000));
    }
  }, [needReconnect]);

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnload, true);

    setRegisteredWS(registeredWS + 1);

    return () => {
      disconnect();
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);


  return (
    <WebSocketContext.Provider value={{
      isConnected,
      subscribe,
      unsubscribe,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  return useContext(WebSocketContext);
};